import bookOpenIcon from "@assets/images/book-open-icon.svg";
import { Box, Button, makeStyles, Typography } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { ScgpUserQuery, useParentGroupsQuery } from "@saleor/graphql";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";

import { UserDetailMode } from "../constants";

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem"
    },
    header: {
      padding: "1rem 1.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      alignItems: "center"
    },
    title: {
      color: "white",
      marginRight: "4px",
      fontSize: 20
    },
    searchBox: {
      padding: "1.6rem"
    },
    searchInput: {
      display: "flex"
    },
    info: {
      marginTop: "2rem",
      display: "flex",
      alignItems: "center",
      "& img": {
        marginRight: "0.7rem"
      }
    },
    button: {
      height: "4rem",
      minWidth: "auto",
      backgroundColor: theme.palette.grey[900],
      "&:active": {
        background: theme.palette.grey[700],
        outline: "none",
        border: `${theme.palette.grey[900]} solid 3px`,
        borderRadius: "4px",
        transition: "0s"
      },
      "&:hover": {
        backgroundColor: theme.palette.grey[700]
      }
    },
    errorMessage: {
      color: theme.palette.error.light,
      lineHeight: "18px",
      marginTop: 4
    },
    successMessage: {
      color: theme.palette.success.main
    },
    cannotClick: {
      border: `1px solid ${theme.palette.background.default}`,
      background: theme.palette.background.default,
      pointerEvents: "none",
      "& fieldset": {
        borderColor: theme.palette.background.default
      },
      "& input": {
        color: theme.palette.grey[900]
      }
    },
    inputRoot: {
      height: "40px"
    },
    selectField: {
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        color: theme.palette.grey[800]
      }
    },
    boxInfo: {
      margin: "8px 0",
      padding: "10px 15px",
      background: theme.palette.background.default,
      borderRadius: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    },
    viewGroup: {
      color: theme.palette.grey[900],
      fontWeight: 700
    },
    userRolesGuidebtn: {
      marginTop: "1.6rem"
    },
    btnActionSecondary: {
      height: 40,
      "&.Mui-disabled": {
        "& .MuiTypography-root": {
          color: "lightgray"
        }
      }
    },
    flex: {
      display: "flex",
      alignItems: "center"
    },
    bookOpenIcon: {
      marginRight: "8px",
      display: "flex",
      alignItems: "center"
    }
  }),
  { name: "UserDetailGroup" }
);

export interface UserDetailGroupProps {
  user?: ScgpUserQuery;
  mode: UserDetailMode;
  groupId?: string;
  setGroupId?: (_) => void;
  setGroupCode?: (_) => void;
}
const UserDetailGroup: React.FC<UserDetailGroupProps> = ({
  mode,
  groupId,
  setGroupId,
  setGroupCode
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const { data } = useParentGroupsQuery();
  const groupChoices = useMemo(
    () =>
      data?.parentGroups?.edges
        .map(({ node }) => ({
          label: node.name,
          value: node.id,
          code: node.code
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1)) || [],
    [data]
  );
  const groupDetails = groupChoices.find(x => x.value === groupId);
  const groupChoicesFiltered = groupChoices.filter(
    item => item.code !== "CUSTOMER"
  );

  return (
    <>
      <Box className={classes.root}>
        <div className={classes.header} data-testid="header">
          <Typography variant="h6" className={classes.title}>
            {intl.formatMessage({
              defaultMessage: "User Group"
            })}
          </Typography>
          {mode !== UserDetailMode.VIEW && (
            <span className={classes.title}>(Required)</span>
          )}
        </div>
        <div className={classes.searchBox}>
          {mode === UserDetailMode.VIEW ||
          (mode === UserDetailMode.EDIT &&
            groupDetails?.code === "CUSTOMER") ? (
            <div className={classes.boxInfo} data-testid="readonly-group">
              <MyTypography variant="body3" className={classes.viewGroup}>
                {groupDetails?.label}
              </MyTypography>
            </div>
          ) : (
            <SingleSelectField
              className={classes.selectField}
              choices={
                mode === UserDetailMode.EDIT
                  ? groupChoicesFiltered
                  : groupChoices
              }
              onChange={e => {
                setGroupId(e.target.value);
                const groupCode = data?.parentGroups?.edges
                  .map(({ node }) => ({
                    id: node.id,
                    code: node.code
                  }))
                  .find(x => x.id === e.target.value).code;
                setGroupCode(groupCode);
              }}
              placeholder={"เลือก User Group"}
              value={groupId !== "create" ? groupId : null}
              name={"group"}
              id="group"
              testId="group"
              InputProps={{
                inputProps: {
                  "data-testid": "input-group"
                }
              }}
            />
          )}
        </div>
      </Box>
      <div className={classes.userRolesGuidebtn}>
        <Button
          color="secondary"
          variant="outlined"
          className={classes.btnActionSecondary}
          fullWidth={true}
          onClick={() =>
            window.open("/dashboard/user-management/user-role-guide", "_blank")
          }
          disabled={false}
          id="userRolesGuideButton"
          name="userRolesGuideButton"
        >
          <Typography variant="body1" className={classes.flex}>
            <span className={classes.bookOpenIcon}>
              <img src={bookOpenIcon} alt="" />
            </span>
            <span>ดูรายละเอียด User Roles</span>
          </Typography>
        </Button>
      </div>
    </>
  );
};

export default UserDetailGroup;
